/**
 * A field you can either type into or pick from.
 *
 * The control for a value that IS free text but usually is not new: a ticket's
 * department is the case it was built for. A plain input made every operator
 * retype (and eventually mistype) the same three words, and a `<select>` would
 * have been a lie - the column is a string, every historical ticket holds
 * whatever was typed into it, and an enum now would orphan all of them.
 *
 * So: an input with a menu hanging off it. The list narrows as you type, Enter
 * takes the highlighted suggestion, and anything typed that matches nothing is
 * still a perfectly good value - which is the half a `<select>` cannot do and
 * the half `MenuField` deliberately does not offer.
 *
 * Built on the same shape as `ClientPicker` (input, absolute panel, mousedown
 * suppressed so the click lands before the blur) rather than on `Pop`: `Pop`
 * takes a button as its trigger and hands focus around, which is exactly wrong
 * for a control whose trigger is a text cursor. Two autocompletes that behave
 * differently in one dialog is the thing this repo has been avoiding since the
 * register name input, so this one copies the picker's manners on purpose.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@wa/ui";

export function ComboField({
  value,
  onChange,
  onPick,
  options,
  placeholder,
  label,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  /**
   * Fired only when a suggestion is CHOSEN, on top of `onChange`.
   *
   * Typing is a draft and choosing is a decision, and a field whose owner saves
   * on blur needs to tell them apart: picking an option leaves focus in the
   * input (the panel suppresses its own mousedown so the click can land), so no
   * blur follows, and without this the choice would sit unsaved until the
   * operator happened to click somewhere else.
   */
  onPick?: (v: string) => void;
  /** Suggestions, in the order they should be offered. */
  options: readonly string[];
  placeholder?: string;
  /** Accessible name for the field - the label above it is a plain span. */
  label: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  // Narrowed by what has been typed, unless what has been typed is exactly one
  // of the options - then the full list comes back, so a second look after
  // picking is not a dead panel showing the one thing already chosen.
  const shown = useMemo(() => {
    const needle = value.trim().toLocaleLowerCase("et");
    if (!needle || options.some((o) => o.toLocaleLowerCase("et") === needle)) return options;
    return options.filter((o) => o.toLocaleLowerCase("et").includes(needle));
  }, [options, value]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // The highlight follows the list, not the keyboard alone: typing narrows the
  // options under it, and an index left pointing past the end would make Enter
  // do nothing on a panel that visibly has entries in it.
  useEffect(() => setActive(0), [shown.length]);

  function pick(option: string) {
    onChange(option);
    onPick?.(option);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (shown.length === 0) return;
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + shown.length) % shown.length);
      return;
    }
    // Enter picks the highlight only while the menu is up. Otherwise it belongs
    // to the form, and swallowing it would break submit-from-the-field.
    if (e.key === "Enter" && open && shown[active]) {
      e.preventDefault();
      pick(shown[active]);
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <label className="crm-inp">
        <input
          id={id}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => options.length > 0 && setOpen(true)}
          // Focus alone is not enough. After picking, the field keeps focus, so
          // a second click on it would fire no `focus` event and the list would
          // stay shut - which reads as a control that has stopped working.
          onClick={() => options.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {/* Decoration, not a control. A `<button>` here would sit inside the
            `<label>` and a click on it activates the label as well - focusing
            the input, opening the list, and then the button's own handler
            closing it again. The whole field is already the trigger. */}
        <ChevronDown
          aria-hidden
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </label>

      {open && shown.length > 0 ? (
        <ul
          role="listbox"
          className="absolute z-50 mt-2 max-h-64 w-full overflow-auto rounded-3xl bg-[var(--crm-surface)] p-1.5 shadow-[var(--crm-sh-lg)]"
        >
          {shown.map((option, i) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === value}
                // Keep focus in the field until the click registers.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(option)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-2xl px-3.5 py-2.5 text-left",
                  i === active && "bg-[var(--crm-sunk)]",
                )}
              >
                <span className="crm-prow-n crm-trunc min-w-0 flex-1">{option}</span>
                {option === value ? <Check className="size-4 shrink-0" /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
